import { useState, useEffect } from 'react'

/**
 * useDebounce - Debounces a value by delaying updates
 *
 * This hook delays updating the debounced value until after the specified
 * delay period has elapsed without the value changing. Useful for search
 * inputs to avoid triggering API calls or expensive computations on every keystroke.
 *
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * const [searchInput, setSearchInput] = useState('')
 * const debouncedSearch = useDebounce(searchInput, 300)
 *
 * useEffect(() => {
 *   // This runs 300ms after user stops typing
 *   filterClients(debouncedSearch)
 * }, [debouncedSearch])
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Set up timeout to update debounced value
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Clean up timeout if value changes before delay elapses
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
